import { RecurringTypesEnum, SubscriptionProductNamesEnum } from "dash-common";
import { SubscriptionProduct } from "gql/generated.graphql";
import { TFunction } from "i18next";
import { getSubscriptionTheme } from "../SubscriptionComponents.constants";

export function getDisplayName(productName: string) {
  if (productName === SubscriptionProductNamesEnum.PRO) return "DASH Pro";
  if (productName === SubscriptionProductNamesEnum.EXCHANGE)
    return "DASH eXchange";
  if (productName.startsWith("DASH")) return productName;
  return `DASH ${productName}`;
}

export function getCardColors(productName: string) {
  const theme = getSubscriptionTheme(productName);
  return {
    bgColor: theme.bg,
    titleColor: theme.title,
    textColor: theme.text,
    priceColor: theme.price,
    buttonBg: theme.buttonBg,
    buttonBgHover: theme.buttonBgHover,
    buttonText: theme.buttonText,
    bookDemoColor: theme.accent,
  };
}

export function getPricePerMonth(product?: SubscriptionProduct) {
  if (!product) return 0;

  // Calculate monthly price based on recurring type
  if (product.recurringType === RecurringTypesEnum.YEAR) {
    return product.price / 12;
  } else if (product.recurringType === RecurringTypesEnum.SIXMONTH) {
    return product.price / 6;
  }

  // Monthly or Free - return price as is
  return product.price;
}

// DSH-900: the annual plan's 10% discount is delivered as a default coupon on
// the product (the Stripe price itself is the full amount), so read it here to
// reflect it in displayed pricing. Returns the active default coupon's
// percentOff for YEAR plans, otherwise 0.
export function getDefaultAnnualPercentOff(
  product?: SubscriptionProduct
): number {
  if (!product || product.recurringType !== RecurringTypesEnum.YEAR) return 0;
  const coupon = product.coupons?.find((c) => c?.active && c?.percentOff);
  return coupon?.percentOff ?? 0;
}

// Monthly price shown on the card, with the default annual discount applied so
// the annual plan shows its real (discounted) per-month price.
export function getDisplayPricePerMonth(product?: SubscriptionProduct): number {
  const base = getPricePerMonth(product);
  const percentOff = getDefaultAnnualPercentOff(product);
  return percentOff ? base * (1 - percentOff / 100) : base;
}

interface IGetButtonTextParams {
  isCurrent?: boolean;
  isTrialing: boolean;
  canUserTrial?: boolean;
  productTrial?: number | null;
  isBasicTier: boolean;
  isExchange: boolean;
  isPro: boolean;
  productName: string;
  t: TFunction<["pages", "common"], undefined>;
}

export function getButtonText({
  isCurrent = false,
  isTrialing,
  canUserTrial,
  productTrial,
  isBasicTier,
  isExchange,
  isPro,
  productName,
  t,
}: IGetButtonTextParams) {
  if (isCurrent && !isTrialing) {
    return t("pages:subscriptionPlans.yourCurrentPlan");
  }
  // Optimistic trial UI: Show trial button if product has trial, unless explicitly denied
  // This gives better UX for new users (99% case) vs users who already trialed
  if (productTrial && canUserTrial !== false) {
    return t("pages:subscriptionPlans.startFreeTrial");
  }
  if (isPro) {
    return t("pages:subscriptionPlans.getPlan", { plan: "Pro" });
  }
  return t("pages:subscriptionPlans.getPlan", { plan: productName });
}

export function getPlanMap() {
  return {
    [SubscriptionProductNamesEnum.BASIC]: "basic",
    [SubscriptionProductNamesEnum.DIRECT]: "basic",
    [SubscriptionProductNamesEnum.PRO]: "pro",
    [SubscriptionProductNamesEnum.EXCHANGE]: "exchange",
    [SubscriptionProductNamesEnum.API]: "api",
  };
}

export function getPlanKey(productName: string) {
  const planMap = getPlanMap();
  return planMap[productName as SubscriptionProductNamesEnum] || "basic";
}

export function getFeaturesSectionTitle(
  planKey: string,
  t: TFunction<["pages", "common"], undefined>
) {
  return t(`common:subscriptionProductsCardsSectionTitles.${planKey}`);
}

export function getRemainingTrialDays(
  trialEndAt?: Date | string | null
): number | null {
  if (!trialEndAt) return null;
  const trialEnd = new Date(trialEndAt);
  const now = new Date();
  const diffTime = trialEnd.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays > 0 ? diffDays : 0;
}
